import { useAtom } from 'jotai';

import { createPalette, PaletteButton, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    type ButtonProps,
    DialogRoot,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { createFastCacheKeyWithCollisionRisk } from '@gitbook/util-suspense-cache';

import { apiResource, readAPICache } from '../../state';
import {
    AdaptiveContentVisitorSegmentsDialog,
    DEFAULT_SEGMENT_TITLE,
} from './AdaptiveContentVisitorSegmentsDialog';
import { siteVisitorSegmentSelector } from './state';
import { useSiteVisitorIsAuthenticated } from './useSiteVisitorIsAuthenticated';
import { useUserDefinedVisitorSegments } from './useUserDefinedVisitorSegments';

const DEFAULT_TITLE = 'Default experience';
const ANONYMOUS_TITLE = 'Anonymous visitor';
const SIGNED_IN_TITLE = 'Signed-in visitor';

/**
 * Button to select the visitor claims to use for the preview of a site.
 */
export function SiteVisitorClaimsButton(
    props: {
        organizationId: string;
        siteId: string;
    } & Partial<ButtonProps>
) {
    const { organizationId, siteId, ...buttonProps } = props;
    const { userDefinedSegments, addVisitorSegment } = useUserDefinedVisitorSegments();
    const [visitorSegment, setVisitorSegment] = useAtom(siteVisitorSegmentSelector(siteId));
    const { isAuthenticated, alwaysAuthenticated, setAuthenticated } =
        useSiteVisitorIsAuthenticated({ organizationId, siteId });

    const visitorSegmentsDialogTrigger = useDialogTriggerState();

    // A site that authenticates every visitor before serving content has no anonymous one to
    // preview, so the choice there is only about the claims they carry.
    const noSegmentTitle = alwaysAuthenticated
        ? DEFAULT_TITLE
        : isAuthenticated
          ? SIGNED_IN_TITLE
          : ANONYMOUS_TITLE;

    return (
        <ButtonGroup kind="segmented">
            <PaletteButton
                kind="secondary"
                {...buttonProps}
                className="w-40 truncate"
                label={visitorSegment?.title ?? noSegmentTitle}
                trailingIcon={Icon.ChevronDown}
                palette={() => {
                    return createPalette({
                        groups: [
                            {
                                key: 'defaultSegmentGroup',
                                commands: alwaysAuthenticated
                                    ? [
                                          {
                                              key: 'default',
                                              title: DEFAULT_TITLE,
                                              selected: visitorSegment === null,
                                              type: PaletteCommandType.RADIO,
                                              trigger: () => {
                                                  setVisitorSegment(null);
                                              },
                                          },
                                      ]
                                    : [
                                          {
                                              key: 'anonymous',
                                              title: ANONYMOUS_TITLE,
                                              subtitle: 'Not signed in to your site',
                                              selected: visitorSegment === null && !isAuthenticated,
                                              type: PaletteCommandType.RADIO,
                                              trigger: () => {
                                                  setVisitorSegment(null);
                                                  setAuthenticated(false);
                                              },
                                          },
                                          {
                                              key: 'signed-in',
                                              title: SIGNED_IN_TITLE,
                                              subtitle: 'Signed in, with no claims',
                                              selected: visitorSegment === null && isAuthenticated,
                                              type: PaletteCommandType.RADIO,
                                              trigger: () => {
                                                  setVisitorSegment(null);
                                                  setAuthenticated(true);
                                              },
                                          },
                                      ],
                            },
                            {
                                key: 'userDefinedSegmentsGroup',
                                title: 'Custom segments',
                                commands: () => {
                                    return (userDefinedSegments || []).map((segment) => {
                                        return {
                                            key: String(segment.id),
                                            title: segment.title,
                                            type: PaletteCommandType.RADIO,
                                            selected: visitorSegment?.id === segment.id,
                                            trigger: () => {
                                                setVisitorSegment({
                                                    type: 'user-defined',
                                                    ...segment,
                                                });
                                            },
                                        };
                                    });
                                },
                            },
                            {
                                key: 'detectedSegmentsGroup',
                                title: 'Detected segments',
                                commands: () => {
                                    const data = readAPICache(
                                        apiResource('listSiteVisitorSegments', {
                                            organizationId,
                                            siteId,
                                        })
                                    );
                                    return data.items.map((segment) => {
                                        const segmentId = createFastCacheKeyWithCollisionRisk(
                                            segment.claims
                                        );
                                        return {
                                            key: segmentId,
                                            title: segment.title,
                                            type: PaletteCommandType.RADIO,
                                            selected: visitorSegment?.id === segmentId,
                                            trigger: () => {
                                                setVisitorSegment({
                                                    id: segmentId,
                                                    type: 'detected',
                                                    ...segment,
                                                });
                                            },
                                        };
                                    });
                                },
                            },
                            {
                                key: 'newSegmentGroup',
                                commands: [
                                    {
                                        key: 'newSegment',
                                        title: 'New segment',
                                        icon: Icon.Download,
                                        trigger: async () => {
                                            const addedSegment = await addVisitorSegment({
                                                title: DEFAULT_SEGMENT_TITLE,
                                                claims: {},
                                            });
                                            setVisitorSegment({
                                                type: 'user-defined',
                                                ...addedSegment,
                                            });
                                            visitorSegmentsDialogTrigger.open();
                                        },
                                    },
                                ],
                            },
                        ],
                    });
                }}
            />
            {visitorSegment ? (
                <>
                    <Button
                        kind="secondary"
                        {...buttonProps}
                        iconOnly
                        leadingIcon={Icon.Settings}
                        label="Edit segment"
                        onPress={async () => {
                            if (visitorSegment.type === 'detected') {
                                const addedSegment = await addVisitorSegment({
                                    title: `${visitorSegment.title} copy`,
                                    claims: visitorSegment.claims,
                                });
                                setVisitorSegment({
                                    type: 'user-defined',
                                    ...addedSegment,
                                });
                            }
                            visitorSegmentsDialogTrigger.open();
                        }}
                    />
                    <DialogRoot {...visitorSegmentsDialogTrigger.rootProps}>
                        <AdaptiveContentVisitorSegmentsDialog
                            organizationId={organizationId}
                            siteId={siteId}
                            selectedSegment={visitorSegment}
                            onConfirm={(selectedSegment) => setVisitorSegment(selectedSegment)}
                        />
                    </DialogRoot>
                </>
            ) : null}
        </ButtonGroup>
    );
}
